import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiLock, FiCpu, FiUser, FiFileText, 
  FiBook, FiCalendar, FiActivity, FiShield, 
  FiX, FiCheck, FiArrowRight, FiZap, FiHeart 
} from 'react-icons/fi';
import { FaRobot, FaGlobe, FaShieldAlt, FaBrain, FaUserNurse, FaChartLine } from 'react-icons/fa'; 
import '../pages/landingpage.css';
import AppHeader from '../components/AppHeader';

import Section1 from "../images/img-section1.png";
import Section1Webp from "../images/img-section1.webp";
import Section31 from "../images/img-section3-1.png";
import Section32 from "../images/img-section3-2.png";
import Section33 from "../images/img-section3-3.png";
import Section34 from "../images/img-section3-4.png";
import Section35 from "../images/img-section3-5.png";
import Section4 from "../images/img-section4.png";
import Section4Webp from "../images/img-section4.webp";
import Section6 from "../images/img-section6.png";
import Section6Webp from "../images/img-section6.webp";
import Footer from "../components/AppFooter.jsx";


const useOnScreen = (options) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, options]);

  return [ref, isVisible];
};

const AnimatedSection = ({ children, className = "", animationClass = "fade-up", style = {} }) => {
  const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
  return (
    <div ref={ref} className={`${className} ${isVisible ? `animate ${animationClass}` : 'initial-state'}`} style={style}>
      {children}
    </div>
  );
};

const ParticleBackground = () => (
  <div className="particle-container">
    <div className="particle particle-1"></div>
    <div className="particle particle-2"></div>
    <div className="particle particle-3"></div>
    <div className="particle particle-4"></div>
    <div className="particle particle-5"></div>
    <div className="particle particle-6"></div>
    <div className="particle particle-7"></div>
    <div className="particle particle-8"></div>
    <div className="particle particle-9"></div>
    <div className="particle particle-10"></div>
  </div>
);

const GridBackground = () => (
  <div className="grid-background">
    <div className="grid-fade"></div>
  </div>
);

const GlowingOrb = ({ className }) => (
  <div className={`glow-orb ${className}`}></div>
);

const LandingPage = () => {
  const navigate = useNavigate();

  // Always start at the top when entering this page
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) root.scrollTop = 0;
  }, []);

  return (
    <div className="landing-page">
      <ParticleBackground />
      <GridBackground />

      {/* Header */}
      <AppHeader />

      {/* SECTION 1: HERO */}
      <section id="hero" className="lp-section lp-hero">
        <GlowingOrb className="orb-hero-1" />
        <GlowingOrb className="orb-hero-2" />
        <GlowingOrb className="orb-hero-3" />
        
        <AnimatedSection className="lp-hero-content" animationClass="slide-up">
          <div className="hero-badge">
            <FiZap className="hero-badge-icon" />
            <span>Next-Gen Reproductive Health Platform</span>
          </div>
          <h1 className="lp-hero-title">
            Your Trusted Space for <br />
            <span className="hero-gradient-text">Reproductive Health, Learning, and Support</span>
          </h1>
          <p className="lp-hero-subtitle">
            Powered by AI. Secured by Blockchain-Grade Encryption. Backed by Medical Experts.
          </p>
          <div className="lp-hero-buttons">
            <button className="lp-btn lp-btn-dark" onClick={() => navigate('/terms-and-conditions')}>
              <span>Get Started Securely</span>
              <FiArrowRight className="btn-arrow-icon" />
            </button>            <button className="lp-btn lp-btn-light" onClick={() => navigate('/login')}>
              <span>Welcome Back</span>
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">50K+</span>
              <span className="stat-label">Users Protected</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">AI Assistant</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">99.9%</span>
              <span className="stat-label">Uptime Secure</span>
            </div>
          </div>
        </AnimatedSection>
        
        <AnimatedSection className="lp-hero-image-container" animationClass="zoom-in">
          <div className="image-glow-ring ring-1"></div>
          <div className="image-glow-ring ring-2"></div>
          <div className="image-glow-ring ring-3"></div>
          <picture>
            <source srcSet={Section1Webp} type="image/webp" />
            <img
              src={Section1}
              alt="Community"
              className="lp-hero-image"
              width="871"
              height="317"
            />
          </picture>
        </AnimatedSection>
      </section>
      <div className="lp-hero-footer">
        <div className="hero-footer-gradient"></div>
      </div>

      {/* SECTION 2: PROBLEM / CARDS */}
      <section id="problem" className="lp-section lp-problem">
        <GlowingOrb className="orb-problem-1" />
        <div className="lp-container lp-split">
          <AnimatedSection className="lp-text-content" animationClass="slide-right">
            <div className="section-label">
              <div className="label-dot"></div>
              <span>THE CHALLENGE</span>
            </div>
            <h2 className="lp-section-header">
              Everything<br /> You Need
              for Confident<br/> 
              <span className="gradient-text">Reproductive Health Care</span>
            </h2>
            <p className="lp-section-desc">
              Access trusted education, evidence-based guidance, easy appointment booking, and clear health tracking—all in one secure platform.
            </p>
            <div className="feature-preview-list">
              <div className="preview-item">
                <FiCheck className="preview-check" />
                <span>Curated by Medical Professionals</span>
              </div>
              <div className="preview-item">
                <FiCheck className="preview-check" />
                <span>End-to-End Data Encryption</span>
              </div>
              <div className="preview-item">
                <FiCheck className="preview-check" />
                <span>Personalized Health Insights</span>
              </div>
            </div>
          </AnimatedSection>
          
          <div className="lp-cards-tumble-container">
            <AnimatedSection className="lp-tumble-card card-1" animationClass="tumble-in-1">
              <div className="card-glow"></div>
              <div className="card-icon-circle"><FiX /></div>
              <h3>Information Overload</h3>
              <p>Reproductive health information is scattered across multiple sources, making it difficult to understand and navigate.</p>
            </AnimatedSection>
            
            <AnimatedSection className="lp-tumble-card card-2" animationClass="tumble-in-2">
              <div className="card-glow"></div>
              <div className="card-icon-circle"><FiX /></div>
              <h3>Trust Uncertainty</h3>
              <p>Users often struggle to identify which reproductive health information is accurate and reliable.</p>
            </AnimatedSection>
            
            <AnimatedSection className="lp-tumble-card card-3" animationClass="tumble-in-3">
              <div className="card-glow"></div>
              <div className="card-icon-circle"><FiX /></div>
              <h3>Care Barriers</h3>
              <p>Accessing appointments and maintaining health records is often inconvenient and limited.</p>
            </AnimatedSection>
            
            <AnimatedSection className="lp-tumble-card card-4" animationClass="tumble-in-4">
              <div className="card-glow"></div>
              <div className="card-icon-circle"><FiX /></div>
              <h3>Tracking Overwhelm</h3>
              <p>Monitoring cycles, symptoms, and health patterns can feel confusing and difficult to manage.</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* SECTION 3: FEATURES */}
      <section id="features" className="lp-section lp-features">
        <GlowingOrb className="orb-features-1" />
        <GlowingOrb className="orb-features-2" />
        
        <AnimatedSection className="lp-section-center-header" animationClass="fade-up">
          <div className="section-label section-label-center">
            <div className="label-dot"></div>
            <span>POWERFUL FEATURES</span>
          </div>
          <h2>All the Tools You Need for <span className="gradient-text">Confident Reproductive Health</span></h2>
          <p>Explore features designed to educate, support, and simplify your care.</p>
        </AnimatedSection>

        <div className="lp-features-grid">
          {[
            { image: Section31, title: "Educational Health Hub", desc: "Access articles, infographics, research, and outreach materials—all curated and reviewed by authorized administrators.", icon: <FiBook /> },
            { image: Section32, title: "AI Health Assistant", desc: "Provides non-diagnostic, evidence-based reproductive health guidance and recommends when to seek professional help.", icon: <FaBrain /> },
            { image: Section33, title: "Secure Appointment Scheduling", desc: "Book appointments, check availability, and keep verifiable digital records—all in one secure system.", icon: <FiCalendar /> },
            { image: Section34, title: "Personal Health Tracking", desc: "Log symptoms, moods, habits, and menstrual cycles with visual summaries and patterns over time.", icon: <FiActivity /> },
            { image: Section35, title: "Ethical & Efficient Administration", desc: "Role-based dashboards for content management, appointment oversight, human support, and engagement analytics.", icon: <FaChartLine /> }
          ].map((feature, idx) => (
            <AnimatedSection key={idx} className="lp-feature-card" animationClass="pop-up" style={{ transitionDelay: `${idx * 100}ms` }}>
              <div className="feature-card-glow"></div>
              <div className="lp-feature-image-wrapper">
                <div className="feature-icon-glow"></div>
                <img src={feature.image} alt={feature.title} />
              </div>
              <div className="feature-badge-icon">
                {feature.icon}
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
              <div className="feature-card-border"></div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* SECTION 4: MISSION */}
      <section id="mission" className="lp-section lp-mission">
        <GlowingOrb className="orb-mission-1" />
        <div className="lp-container lp-split">
          <AnimatedSection className="lp-text-content" animationClass="slide-right">
            <div className="section-label">
              <div className="label-dot"></div>
              <span>OUR MISSION</span>
            </div>
            <h2>Built to Educate, Support, <br />and <span className="gradient-text">Protect</span></h2>
            <p>
              Our platform was designed to responsibly support reproductive health by combining education, technology, and human oversight. We aim to improve health literacy, expand access to care, promote self-awareness through tracking, and ensure ethical administration—while keeping users safe, informed, and in control.
            </p>
            <div className="mission-tags">
              <span className="mission-tag"><FaShieldAlt /> Privacy First</span>
              <span className="mission-tag"><FaUserNurse /> Human Support</span>
              <span className="mission-tag"><FaGlobe /> Accessible Care</span>
              <span className="mission-tag"><FiHeart /> Ethical Design</span>
            </div>
          </AnimatedSection>
          <AnimatedSection className="lp-image-right lp-mission-image-container" animationClass="slide-left">
            <div className="mission-image-glow"></div>
            <picture>
              <source srcSet={Section4Webp} type="image/webp" />
              <img
                src={Section4}
                alt="Community Support"
                className="lp-mission-image"
                width="600"
                height="400"
              />
            </picture>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 5: SECURITY & FOOTER */}
      <section id="security" className="lp-section lp-security">
        <GlowingOrb className="orb-security-1" />
        <GlowingOrb className="orb-security-2" />
        
        <AnimatedSection className="lp-section-center-header" animationClass="fade-up">
          <div className="section-label section-label-center">
            <div className="label-dot"></div>
            <span>SECURITY & TRUST</span>
          </div>
          <h2>Your Data, Your <span className="gradient-text">Privacy</span>, Our Priority</h2>
          <p>Security and transparency built into every layer of our platform.</p>
        </AnimatedSection>

        <AnimatedSection className="lp-footer-text" animationClass="fade-up">
          <div className="footer-text-icon"><FiShield /></div>
          <p className="footer-text-heading">Your reproductive health data is personal.</p>
          <p>That's why our platform prioritizes security, transparency, and responsible technology use at every level.</p>
        </AnimatedSection>

        <div className="lp-security-grid">
          {[
            { icon: "🔒", title: "Secure & role-based access control", gradient: "from-blue" },
            { icon: "🧠", title: "AI is non-diagnostic and evidence-based", gradient: "from-cyan" },
            { icon: "👩‍⚕️", title: "Human support for high-risk concerns", gradient: "from-purple" },
            { icon: "📄", title: "Transparent data handling and ethics", gradient: "from-indigo" }
          ].map((item, idx) => (
            <AnimatedSection 
              key={idx} 
              className="lp-security-box" 
              animationClass={idx % 2 === 0 ? "flip-in-x" : "flip-in-y"}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              <div className={`security-glow ${item.gradient}`}></div>
              <span className="lp-sec-icon">{item.icon}</span>
              <h3>{item.title}</h3>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* SECTION 6: JOURNEY CTA */}
      <section id="journey" className="lp-section lp-section6">
        <GlowingOrb className="orb-journey-1" />
        <GlowingOrb className="orb-journey-2" />
        
        <div className="lp-section6-image-container">
          <picture>
            <source srcSet={Section6Webp} type="image/webp" />
            <img
              src={Section6}
              alt="Start Your Journey"
              className="lp-section6-image"
            />
          </picture>
          <div className="lp-section6-overlay"></div>
          <div className="lp-section6-content">
            <AnimatedSection animationClass="fade-up">
              <div className="journey-badge">
                <FiZap className="journey-badge-icon" />
                <span>Ready When You Are</span>
              </div>
              <h2 className="lp-section6-title">
                Take Control of Your Reproductive Health—<br />
                <span className="journey-gradient-text">Safely and Confidently</span>
              </h2>
              <button className="lp-btn lp-btn-light lp-btn-journey" onClick={() => {
                navigate('/terms-and-conditions');
              }}>
                <span>Start Your Journey Today</span>
                <FiArrowRight className="btn-arrow-icon" />
              </button>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
